import type { BillingPlan } from "../types";
import { normalizeAdminLocale } from "../lib/adminLocales";
import { referralProbeContext } from "../utils/shopifyEntryContext";
import { apiFetch } from "./client";
import { canonicalStockyPublicBigIntId } from "./stocky_public_ids";

export function listPlans(): Promise<BillingPlan[]> {
  return apiFetch<BillingPlan[]>("/billing/plans");
}

export interface BillingContext {
  /** Currency UreyukiBox will actually send to appSubscriptionCreate. */
  currency: "JPY" | "USD";
}

export function getBillingContext(): Promise<BillingContext> {
  return apiFetch<BillingContext>("/billing/context");
}

export interface SubscribeResponse {
  shop_id: string;
  plan_code: string;
  subscription_id?: string;
  shopify_charge_id?: string;
  confirmation_url: string | null;
  subscription_status: string;
}

function requireBillingObject(
  value: unknown,
  label: string,
): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} is invalid`);
  }
  return value as Record<string, unknown>;
}

function normalizeSubscribeResponse(value: unknown): SubscribeResponse {
  const response = requireBillingObject(value, "billing subscribe response");
  const normalized = {
    ...response,
    shop_id: canonicalStockyPublicBigIntId(response.shop_id),
  } as SubscribeResponse;
  if (Object.prototype.hasOwnProperty.call(response, "subscription_id")) {
    normalized.subscription_id = canonicalStockyPublicBigIntId(
      response.subscription_id,
    );
  }
  return normalized;
}

export async function subscribe(planCode: string): Promise<SubscribeResponse> {
  // 有料プランはShopify承認後に同じ埋め込み画面へ戻す文脈が必須。
  // Freeには承認画面がないため、文脈を取得できない再インストール途中でも互換を保つ。
  const returnContext = planCode === "free" ? null : referralProbeContext();
  if (planCode !== "free" && returnContext === null) {
    throw new Error("Shopify billing return context is unavailable");
  }
  return normalizeSubscribeResponse(
    await apiFetch<unknown>(`/billing/subscribe`, {
      method: "POST",
      query: {
        plan_code: planCode,
        host: returnContext?.host,
        locale: returnContext ? normalizeAdminLocale(returnContext.locale) : undefined,
      },
    }),
  );
}

export type BillingSubscriptionStatus =
  | "pending_approval"
  | "active"
  | "frozen"
  | "cancelled"
  | "declined"
  | "expired";

interface SyncBillingResponseBase {
  shop_id: string;
  trial_ends_at?: string | null;
}

export type SyncBillingResponse =
  | (SyncBillingResponseBase & {
      active: true;
      status: "active";
      plan_code: string;
      shopify_charge_id: string;
    })
  | (SyncBillingResponseBase & {
      active: false;
      status: Exclude<BillingSubscriptionStatus, "active"> | null;
      plan_code: string | null;
      shopify_charge_id: string | null;
    });

/**
 * Reconcile local entitlements with Shopify's authoritative `activeSubscriptions`.
 *
 * GraphQL app subscriptions auto-activate on merchant approval — there is no
 * separate activation step. The SPA calls this after returning from Shopify's
 * confirmation page (and as a webhook backstop on app load); the shop is
 * identified server-side via the App Bridge session token (Bearer).
 */
function normalizeSyncBillingResponse(value: unknown): SyncBillingResponse {
  const response = requireBillingObject(value, "billing sync response");
  return {
    ...response,
    shop_id: canonicalStockyPublicBigIntId(response.shop_id),
  } as SyncBillingResponse;
}

export async function syncBilling(): Promise<SyncBillingResponse> {
  return normalizeSyncBillingResponse(
    await apiFetch<unknown>(`/billing/sync`, { method: "POST" }),
  );
}
